import { useDraggable } from "@dnd-kit/react";
import { Check, Edit2, GripVertical, RotateCcw, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSidePanel } from "@/components/contexts/SidePanelContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/NewButton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { renderFileIcon } from '@/lib/document-icons';
import { useBundleTreeActions, useDuplicateMap } from "../bundleTreeStore";
import type { FileNode } from "../bundleTypes";
import { DuplicateDocumentWarning } from "../DuplicateDocumentWarning";
import { RowActions } from "../RowActions";
import { useSingleDoubleClick } from "../useSingleDoubleClick";
import { supportsBundlePageRange } from "../bundleEligibility";

type EditFileProps = {
  file: FileNode;
}

export const EditFile = ({file}: EditFileProps) => {
  const duplicateMap = useDuplicateMap();
  const { updateFile: onUpdateFile, removeFile: onRemoveFile } = useBundleTreeActions();
  const { openDocumentPanel, isOpen, activeDocumentGuid } = useSidePanel();
  const isActive = isOpen && activeDocumentGuid === file.fileReferenceGuid;
  const { ref, handleRef, isDragSource } = useDraggable({
    id: file.fileId,
    data: { type: 'file', bundleFileId: file.fileId, groupId: file.parentGroupId },
  });

  const supportsPageRange = supportsBundlePageRange(file.fileExtension);
  const [editing, setEditing] = useState<'name' | 'pages' | null>(null);
  const [draft, setDraft] = useState('');

  const otherOccurrences = duplicateMap[file.fileReferenceGuid]?.filter(e => e.fileId !== file.fileId) ?? [];

  const nameClickHandlers = useSingleDoubleClick(
    () => openDocumentPanel({ documentGuid: file.fileReferenceGuid }),
    () => startEditing('name'),
  );

  const startEditing = (field: 'name' | 'pages') => {
    setDraft(field === 'name' ? (file.customName || file.fileName) : (file.pageRange || ''));
    setEditing(field);
  };

  const cancel = () => setEditing(null);

  const commit = () => {
    if (editing === 'name') {
      const trimmed = draft.trim();
      const isOriginal = !trimmed || trimmed === file.fileName;
      onUpdateFile(file.fileId, { customName: isOriginal ? null : trimmed });
    } else if (editing === 'pages') {
      const stripped = draft.replace(/\s/g, '');
      if (stripped && !isValidPageRange(stripped)) {
        toast.error('Invalid page range. Use format: 1-5, 8, 12-15');
        return;
      }
      onUpdateFile(file.fileId, { pageRange: stripped || null });
    }
    setEditing(null);
  };

  return (
    <div ref={ref} className={`relative flex items-center gap-2 px-2 py-1.5 rounded-sm border text-sm group transition-colors ${isDragSource ? 'opacity-30 bg-gray-50' : isActive ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
      <GripVertical
        ref={handleRef}
        data-testid="bundle-file-drag-handle"
        className="h-3.5 w-3.5 text-gray-300 shrink-0 cursor-grab outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:rounded-sm"
      />
      <div className="shrink-0 cursor-pointer" onClick={() => openDocumentPanel({ documentGuid: file.fileReferenceGuid })}>{renderFileIcon(`file${file.fileExtension}`, 'h-4 w-4')}</div>
      <DuplicateDocumentWarning otherOccurrences={otherOccurrences} />
      {editing === 'name' ? (
        <div className="flex items-center gap-1 flex-1" onKeyDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
            onBlur={commit}
            className="h-6 text-sm flex-1"
            autoFocus
          />
          <Button
            icon={Check}
            variant="bare"
            size="icon-sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            className="text-green-600 hover:text-green-800"
          />
          <Button
            icon={X}
            variant="bare"
            size="icon-sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancel}
            className="text-gray-400 hover:text-gray-600"
          />
        </div>
      ) : (
        <span
          className="flex-1 truncate text-sm cursor-pointer hover:text-blue-600 transition-colors"
          {...nameClickHandlers}
        >
          {file.customName || file.fileName}
          {file.customName && <span className="text-xs text-gray-400 ml-1">(custom)</span>}
        </span>
      )}
      {supportsPageRange && (editing === 'pages' ? (
        <div className="flex items-center gap-1 shrink-0" onKeyDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <span className="text-xs text-gray-400">pp.</span>
          <Input
            value={draft}
            onChange={(e) => { if (/^[\d\s,-]*$/.test(e.target.value)) setDraft(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
            onBlur={commit}
            className="h-6 text-xs w-20"
            placeholder="e.g. 1-5"
            autoFocus
          />
          <Button
            icon={Check}
            variant="bare"
            size="icon-sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            className="text-green-600 hover:text-green-800"
          />
          <Button
            icon={X}
            variant="bare"
            size="icon-sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancel}
            className="text-gray-400 hover:text-gray-600"
          />
        </div>
      ) : (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-xs text-gray-400 shrink-0 cursor-pointer hover:text-gray-600"
                onClick={() => startEditing('pages')}
              >
                {file.pageRange ? `pp. ${file.pageRange}` : <span className="opacity-0 group-hover:opacity-100 transition-opacity">+ pages</span>}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[220px] text-xs font-normal z-100000">
              Select which pages to include. Use commas and ranges, e.g. 1-5, 8, 12-15. Leave empty for all pages.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
      <RowActions
        hoverGroup="group-hover"
        actions={[
          { icon: Edit2, label: 'Rename', onClick: () => startEditing('name'), visible: !editing },
          { icon: RotateCcw, label: 'Revert to original name', onClick: () => onUpdateFile(file.fileId, { customName: null }), visible: !editing && !!file.customName, className: 'text-gray-400 hover:text-blue-600' },
          { icon: Trash2, label: 'Remove', onClick: () => onRemoveFile(file.fileId), danger: true },
        ]}
      />
    </div>
  );
}

/** Validate page range format: digits, commas, single dashes between digits. e.g. "1-5,8,12-15". All page numbers must be >= 1. */
function isValidPageRange(stippedValue: string): boolean {
  if (!/^(\d+(-\d+)?)(,\d+(-\d+)?)*$/.test(stippedValue)) return false;
  for (const part of stippedValue.split(',')) {
    if (part.includes('-')) {
      const [a, b] = part.split('-').map(Number);
      if (a < 1 || a > b) return false;
    } else {
      if (Number(part) < 1) return false;
    }
  }
  return true;
}
